// RT-18: Lazy email verification banner.
// Shown after 7 days without email confirmation; feature-limits after 30 days.

import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface VerificationStatus {
  daysSince: number;
  isLimited: boolean; // true after 30 days without confirmation
}

function getVerificationStatus(createdAt: string, emailConfirmedAt: string | null | undefined): VerificationStatus | null {
  if (emailConfirmedAt) return null; // already verified, no banner
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  const daysSince = Math.floor((now - created) / 86_400_000);
  if (daysSince < 7) return null; // under 7 days, grace period, no banner
  return { daysSince, isLimited: daysSince >= 30 };
}

export function EmailVerificationBanner() {
  const { user, profile } = useAuth();
  const [sending, setSending]   = useState(false);
  const [sent,    setSent]      = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!user || !profile || dismissed) return null;

  const status = getVerificationStatus(profile.created_at, user.email_confirmed_at);
  if (!status) return null;

  async function resend() {
    if (!user?.email) return;
    setSending(true);
    await supabase.auth.resend({ type: 'signup', email: user.email });
    setSending(false);
    setSent(true);
  }

  const bannerColor = status.isLimited ? 'rgba(239,68,68,0.12)' : 'rgba(31, 78, 95, 0.08)';
  const borderColor = status.isLimited ? 'rgba(239,68,68,0.3)' : 'var(--line-medium)';
  const textColor   = status.isLimited ? '#fca5a5' : 'var(--petrol)';

  return (
    <div style={{
      backgroundColor: bannerColor,
      border: `1px solid ${borderColor}`,
      borderRadius: 'var(--radius-md)',
      padding: '0.75rem 1rem',
      margin: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1 }}>
        <p style={{ color: textColor, fontSize: 'var(--text-caption)', fontWeight: 600, margin: 0 }}>
          {status.isLimited
            ? 'Acesso limitado, confirme seu email para reativar todos os recursos'
            : 'Confirme seu email para garantir acesso contínuo →'}
        </p>
        {status.isLimited && (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.2rem 0 0' }}>
            Após 30 dias sem confirmação, recursos pagos ficam restritos.
          </p>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {sent ? (
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Email enviado ✓</span>
        ) : (
          <button
            className="btn btn-secondary btn-sm"
            onClick={resend}
            disabled={sending}
            style={{ whiteSpace: 'nowrap' }}
          >
            {sending ? 'Enviando…' : 'Reenviar email'}
          </button>
        )}
        {!status.isLimited && (
          <button
            onClick={() => setDismissed(true)}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0 0.25rem', fontSize: '1rem' }}
            aria-label="Fechar aviso"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
